// Bilder (Pictures) page - gallery of photos from club events
// Only authenticated users can upload images
// Images are organized by event with a professional image viewer

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase-config';
import { FiUpload, FiTrash2, FiX, FiChevronLeft, FiChevronRight, FiCheck, FiImage } from 'react-icons/fi';
import { SmartImage, shrinkImage, uploadThumbnails, deleteThumbnails } from './imageUtils';

const Bilder = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [expandedDescId, setExpandedDescId] = useState(null);
  const galleryRef = useRef(null);
  const touchStartX = useRef(null);
  const auth = getAuth(app);

  const selectedEvent = events.find(e => e.id === selectedEventId) || null;
  const images = selectedEvent?.images || [];

  const fetchEvents = async () => {
    try {
      const db = getFirestore(app);
      const eventsCollection = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsCollection);
      let eventsList = eventsSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      eventsList = eventsList.map(event => {
        const [year, quarter] = event.Kvartal.split(' ');
        const quarterNumber = parseInt(quarter[1], 10);
        event.parsedKvartal = new Date(year, (quarterNumber - 1) * 3);
        return event;
      });

      eventsList.sort((a, b) => b.parsedKvartal - a.parsedKvartal);
      setEvents(eventsList);
    } catch (error) {
      console.error('Error fetching events: ', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      setUser(currentUser);
      fetchEvents();
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Select the event from ?event=… (e.g. from "Ferske minner"), otherwise the first event with images
  useEffect(() => {
    if (events.length > 0 && !selectedEventId) {
      const wanted = new URLSearchParams(window.location.search).get('event');
      const fromLink = wanted && events.find(e => e.id === wanted);
      const withImages = events.find(e => e.images?.length > 0);
      setSelectedEventId(fromLink?.id || withImages?.id || events[0].id);
    }
  }, [events, selectedEventId]);

  const selectEvent = (eventId) => {
    setSelectedEventId(eventId);
    setSelectMode(false);
    setSelectedImages(new Set());
  };

  const openViewer = (index) => {
    if (selectMode) return;
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
  }, []);

  const goToPrev = useCallback(() => {
    setViewerIndex(i => {
      const len = images.length;
      return len > 0 ? (i > 0 ? i - 1 : len - 1) : 0;
    });
  }, [images.length]);

  const goToNext = useCallback(() => {
    setViewerIndex(i => {
      const len = images.length;
      return len > 0 ? (i < len - 1 ? i + 1 : 0) : 0;
    });
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!viewerOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewerOpen, closeViewer, goToPrev, goToNext]);

  // Lock body scroll when viewer is open
  useEffect(() => {
    document.body.style.overflow = viewerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [viewerOpen]);

  // Touch swipe for viewer
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext(); else goToPrev();
    }
    touchStartX.current = null;
  };

  // Switch event inside the viewer
  const handleViewerEventChange = (eventId) => {
    const newEvent = events.find(e => e.id === eventId);
    setSelectedEventId(eventId);
    setViewerIndex(0);
    if (!newEvent?.images?.length) {
      setViewerOpen(false);
    }
  };

  // Image upload
  const handleImageUpload = async (eventId, fileEvent) => {
    const files = fileEvent.target.files;
    if (!files.length) return;
    if (!user) {
      console.error('User is not authenticated. Cannot upload images.');
      return;
    }

    setUploading(true);
    const storage = getStorage(app);
    const db = getFirestore(app);
    const eventDocRef = doc(db, 'events', eventId);

    // Photos are shrunk to max 2560 px before upload; phones often take 6000+ px images
    const uploadPromises = Array.from(files).map(async original => {
      const file = await shrinkImage(original);
      return new Promise((resolve, reject) => {
        const storageRef = ref(storage, `events/${eventId}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            console.error('Error uploading file:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              await uploadThumbnails(storage, storageRef.fullPath, file);
              await updateDoc(eventDocRef, { images: arrayUnion(downloadURL) });
              setEvents(prev => prev.map(ev =>
                ev.id === eventId
                  ? { ...ev, images: [...(ev.images || []), downloadURL] }
                  : ev
              ));
              resolve();
            } catch (error) {
              console.error('Error updating Firestore:', error);
              reject(error);
            }
          }
        );
      });
    });

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Some uploads failed:', error);
    }
    setUploading(false);
    fileEvent.target.value = '';
  };

  // Single image delete
  const handleImageDelete = async (eventId, imageUrl) => {
    try {
      const storage = getStorage(app);
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      await deleteThumbnails(storage, imageRef.fullPath);

      const db = getFirestore(app);
      const eventDocRef = doc(db, 'events', eventId);
      await updateDoc(eventDocRef, { images: arrayRemove(imageUrl) });

      setEvents(prev => prev.map(ev =>
        ev.id === eventId
          ? { ...ev, images: ev.images.filter(url => url !== imageUrl) }
          : ev
      ));
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        const db = getFirestore(app);
        const eventDocRef = doc(db, 'events', eventId);
        await updateDoc(eventDocRef, { images: arrayRemove(imageUrl) });
        setEvents(prev => prev.map(ev =>
          ev.id === eventId
            ? { ...ev, images: ev.images.filter(url => url !== imageUrl) }
            : ev
        ));
      } else {
        console.error('Error deleting image:', error);
      }
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`Er du sikker på at du vil slette ${selectedImages.size} bilde(r)?`)) return;
    for (const url of selectedImages) {
      await handleImageDelete(selectedEventId, url);
    }
    setSelectedImages(new Set());
    setSelectMode(false);
  };

  const toggleImageSelection = (url) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };

  // Clamp viewer index when images array changes
  useEffect(() => {
    if (viewerOpen && images.length > 0 && viewerIndex >= images.length) {
      setViewerIndex(images.length - 1);
    }
  }, [images.length, viewerIndex, viewerOpen]);

  return (
    <div className="bilder-page">
      <div className="bilder-header">
        <h1>🖼️ Bilder</h1>
      </div>

      {/* Gallery section with event dropdown */}
      {selectedEvent && (
        <div className="bilder-gallery" ref={galleryRef}>
          <div className="bilder-gallery-header">
            <div className="bilder-gallery-title">
              <select
                className="bilder-event-select"
                value={selectedEventId}
                onChange={(e) => selectEvent(e.target.value)}
              >
                {events.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.sted_konsept} — {e.event_type} — {e.Kvartal} ({e.images?.length || 0})
                  </option>
                ))}
              </select>
              <span className="bilder-gallery-meta">
                {images.length} bilde{images.length !== 1 ? 'r' : ''}
              </span>
            </div>
            <div className="bilder-gallery-actions">
              <label className="bilder-btn bilder-btn-upload">
                <FiUpload />
                <span>Last opp</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(selectedEvent.id, e)}
                  hidden
                />
              </label>
              {images.length > 0 && (
                <button
                  className={`bilder-btn${selectMode ? ' bilder-btn-active' : ''}`}
                  onClick={() => { setSelectMode(!selectMode); setSelectedImages(new Set()); }}
                >
                  <FiCheck />
                  <span>{selectMode ? 'Avbryt' : 'Velg'}</span>
                </button>
              )}
              {selectMode && selectedImages.size > 0 && (
                <button className="bilder-btn bilder-btn-danger" onClick={handleBulkDelete}>
                  <FiTrash2 />
                  <span>Slett ({selectedImages.size})</span>
                </button>
              )}
            </div>
          </div>

          {selectedEvent.description?.trim() && (
            <div className="bilder-event-description-wrap">
              <p className={`bilder-event-description${expandedDescId === selectedEventId ? ' expanded' : ''}`}>
                {selectedEvent.description}
              </p>
              {selectedEvent.description.length > 100 && (
                <button
                  type="button"
                  className="bilder-description-toggle"
                  onClick={() => setExpandedDescId(expandedDescId === selectedEventId ? null : selectedEventId)}
                >
                  {expandedDescId === selectedEventId ? 'Vis mindre' : 'Vis mer'}
                </button>
              )}
            </div>
          )}

          {uploading && (
            <div className="bilder-uploading">Laster opp bilder...</div>
          )}

          {images.length > 0 ? (
            <div className="bilder-grid">
              {images.map((url, index) => (
                <div
                  key={index}
                  className={`bilder-thumb${selectMode && selectedImages.has(url) ? ' selected' : ''}`}
                  onClick={() => selectMode ? toggleImageSelection(url) : openViewer(index)}
                >
                  <SmartImage src={url} size={800} />
                  {selectMode && (
                    <div className="bilder-thumb-check">
                      {selectedImages.has(url) && <FiCheck />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bilder-empty">
              <FiImage size={48} />
              <p>Ingen bilder ennå</p>
              <label className="bilder-btn bilder-btn-upload">
                <FiUpload />
                <span>Last opp bilder</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(selectedEvent.id, e)}
                  hidden
                />
              </label>
            </div>
          )}
        </div>
      )}

      {/* Full-screen image viewer */}
      {viewerOpen && images.length > 0 && (
        <div
          className="bilder-viewer"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Toolbar */}
          <div className="bilder-viewer-toolbar">
            <div className="bilder-viewer-toolbar-left">
              <select
                className="bilder-viewer-event-select"
                value={selectedEventId}
                onChange={(e) => handleViewerEventChange(e.target.value)}
              >
                {events.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.event_type} {e.sted_konsept} ({e.images?.length || 0})
                  </option>
                ))}
              </select>
              <label className="bilder-viewer-upload-btn" title="Last opp bilder">
                <FiUpload />
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(selectedEventId, e)}
                  hidden
                />
              </label>
            </div>
            <div className="bilder-viewer-toolbar-center">
              <span className="bilder-viewer-counter">
                {viewerIndex + 1} / {images.length}
              </span>
            </div>
            <div className="bilder-viewer-toolbar-right">
              <button className="bilder-viewer-close" onClick={closeViewer} title="Lukk">
                <FiX />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="bilder-viewer-body" onClick={closeViewer}>
            {images.length > 1 && (
              <button
                className="bilder-viewer-nav bilder-viewer-prev"
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              >
                <FiChevronLeft />
              </button>
            )}
            <SmartImage
              className="bilder-viewer-img"
              src={images[viewerIndex]}
              size={1600}
              loading="eager"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />
            {images.length > 1 && (
              <button
                className="bilder-viewer-nav bilder-viewer-next"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
              >
                <FiChevronRight />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bilder;
